import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Button, Spinner } from 'design-system/react';
import logoLight from 'design-system/src/assets/lean-agility-logo.svg';
import logoDark from 'design-system/src/assets/lean-agility-logo-dark.svg';

import { getSimulationByLabel, getTeams } from '../../client';
import type { TeamResponse } from '../../client';
import { useDocumentTitle } from '../../hooks';
import { DarkModeSwitchButton, TeamSelect } from '../atoms';
import { Page } from '../organisms';

/**
 * Join flow driven entirely by the URL:
 *   /            → EnterLabel (text input, navigates to /join/{label})
 *   /join/:label → resolve label via API → show role/team picker
 */
export function JoinSimulationPage() {
  const { simulationLabel } = useParams<{ simulationLabel?: string }>();

  useDocumentTitle('Join Simulation');

  // URL has :simulationLabel → resolve and show role picker
  if (simulationLabel) {
    return <JoinByLabel label={decodeURIComponent(simulationLabel)} />;
  }

  // No label in URL → show text input
  return <EnterLabel />;
}

// ─── Step 1: Enter simulation label ───────────────────────────────────────

function EnterLabel() {
  const navigate = useNavigate();
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = label.trim();
    if (!trimmed) return;

    setIsLoading(true);
    setError(null);

    try {
      // Validate the label exists before navigating
      await getSimulationByLabel({ path: { label: trimmed }, throwOnError: true });
      navigate(`/join/${encodeURIComponent(trimmed)}`);
    } catch {
      setError(`No simulation found with name "${trimmed}"`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Page title={null} hideBackButton>
      <div className="absolute top-4 right-4">
        <DarkModeSwitchButton />
      </div>

      <div className="flex flex-col items-center mt-12 mb-8">
        <img src={logoLight} alt="Lean Agility" className="w-64 dark:hidden" />
        <img src={logoDark} alt="Lean Agility" className="w-64 hidden dark:block" />
        <h1 className="text-3xl font-extrabold text-gradient mt-4">Value Flow Game</h1>
        <p className="text-sm text-text-secondary mt-1 text-center max-w-xs">
          Experience how work flows through a system and discover what makes teams deliver value faster.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col items-center gap-4 w-full max-w-sm mx-auto">
        <p className="text-text-secondary text-center">Enter the simulation name provided by your trainer.</p>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Simulation name"
          autoFocus
          className="w-full px-4 py-3 rounded-lg border border-border-default bg-bg-elevated text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-action-primary"
        />
        {error && <p className="text-feedback-error text-sm">{error}</p>}
        <Button variant="primary" type="submit" disabled={isLoading || !label.trim()}>
          {isLoading ? 'Looking up...' : 'Join'}
        </Button>
      </form>
    </Page>
  );
}

// ─── Step 2: Resolve label + choose role/team ─────────────────────────────

function JoinByLabel({ label }: { label: string }) {
  const navigate = useNavigate();

  const {
    data: simulation,
    isLoading: simLoading,
    isError: simError,
  } = useQuery({
    queryKey: ['simulation-by-label', label],
    queryFn: () => getSimulationByLabel({ path: { label }, throwOnError: true }).then(({ data }) => data),
    retry: 1,
  });

  const simId = simulation?.id;

  const { data: teams, isLoading: teamsLoading } = useQuery({
    queryKey: ['teams', simId],
    queryFn: () => getTeams({ path: { sim_id: simId! }, throwOnError: true }).then(({ data }) => data),
    enabled: !!simId,
  });

  const handleJoinTeam = (team: TeamResponse | null) => {
    if (!team || !simId) return;
    navigate(`/sim/${simId}/${encodeURIComponent(team.name)}/tasks`);
  };

  // Loading simulation
  if (simLoading) {
    return (
      <Page title={null}>
        <div className="flex flex-col items-center gap-3 mt-16">
          <Spinner size="xl" />
          <p className="text-text-secondary">
            Looking up <span className="font-bold">{label}</span>...
          </p>
        </div>
      </Page>
    );
  }

  // Label not found
  if (simError || !simulation) {
    return (
      <Page title={null}>
        <div className="flex flex-col items-center gap-4 mt-16">
          <p className="text-feedback-error">No simulation found with name &ldquo;{label}&rdquo;</p>
          <Link to="/" className="text-sm text-action-primary hover:underline">
            &larr; Try a different name
          </Link>
        </div>
      </Page>
    );
  }

  // Simulation resolved → show role picker
  return (
    <Page title={null}>
      <div className="absolute top-4 right-4">
        <DarkModeSwitchButton />
      </div>

      <div className="flex flex-col items-center mt-12 mb-8">
        <img src={logoLight} alt="Lean Agility" className="w-64 dark:hidden" />
        <img src={logoDark} alt="Lean Agility" className="w-64 hidden dark:block" />
        <h1 className="text-3xl font-extrabold text-gradient mt-4">{simulation.label}</h1>
      </div>

      <div className="flex flex-col items-center gap-6 w-full max-w-sm mx-auto">
        {/* Participant section */}
        <div className="w-full">
          <p className="text-sm font-medium mb-2 text-center">Join as participant:</p>
          {teamsLoading ? (
            <Spinner size="md" />
          ) : teams && teams.length > 0 ? (
            <TeamSelect teams={teams} onTeamSelected={handleJoinTeam} />
          ) : (
            <p className="text-text-secondary text-sm text-center">No teams available yet.</p>
          )}
        </div>

        {/* Back link */}
        <Link to="/" className="text-sm text-text-muted hover:text-text-primary transition-colors">
          &larr; Choose a different simulation
        </Link>
      </div>
    </Page>
  );
}
